import { useEffect, useMemo, useRef, useState } from 'react';
import { color } from '../styles/theme';
import { dateTime } from '../lib/format';
import { makeLookup } from '../lib/presenters';
import { statusLabelOf } from '../lib/pipeline';
import { contextOf, fillTemplate, templatesFor } from '../lib/templates';
import { registryFieldsOf, registryValue } from '../lib/presenters';
import { useMessages, useSendMessage, type Message, type SendMessageBody } from '../api/conversation';
import { useSetStatus } from '../api/queries';
import type { MetaResponse, SubmissionDetail as SubmissionDetailResponse } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import {
  Button,
  Chip,
  Confirm,
  ErrorNote,
  Field,
  Loading,
  SectionHead,
  inputStyle,
  selectStyle,
} from '../components/ui';

/**
 * Answering an inquiry, in the dashboard.
 *
 * Every form on the public site used to end at a `mailto:` link: staff left
 * the record, wrote in their own mail client, and nothing came back — the next
 * coordinator could not tell whether anyone had replied, or what was said. So
 * the reply is written here, and the thread lives on the record.
 *
 * One timeline, not two. Status moves and messages are the same story told in
 * order, and splitting them into a History list and a Messages list makes a
 * reader reconcile two clocks to answer "what happened to this person".
 *
 * A reply goes from the mailbox that owns the record's stream — the one its
 * receipt came from — and a copy stays in that mailbox's Sent Items, so the
 * person's answer arrives beside it.
 *
 * What the person writes back is on the thread too (API 050): the API reads
 * the CRM's mailboxes and files each answer to a CRM email on its record. The
 * line under the thread says whether that reading is on, and when it last
 * looked — an empty thread must not read as "they never answered" when the
 * mailboxes are simply not being read.
 */

/** What staff write here; receipts are the form's own. */
type ComposeKind = SendMessageBody['kind'];

type Submission = SubmissionDetailResponse['submission'];
type FormMeta = SubmissionDetailResponse['meta'];
type History = SubmissionDetailResponse['history'];

type TimelineItem =
  | { kind: 'status'; at: string; actor: string | null; from: string | null; to: string | null }
  | { kind: 'message'; at: string; message: Message };

/** Both records on one rail, oldest first. Ties keep the status move ahead of the reply it triggered. */
function mergeTimeline(history: History, messages: Message[]): TimelineItem[] {
  const items: TimelineItem[] = [
    ...history.map((h) => ({
      kind: 'status' as const,
      at: h.at,
      actor: h.actor ?? null,
      from: h.from ?? null,
      to: h.to ?? null,
    })),
    ...messages.map((m) => ({ kind: 'message' as const, at: m.createdAt, message: m })),
  ];
  return items.sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
}

const deliveryTone: Record<string, { bg: string; fg: string; label: string }> = {
  sent: { bg: '#cfe9d8', fg: '#14563c', label: 'Sent' },
  failed: { bg: '#fdf1ee', fg: '#a4432f', label: 'Failed' },
  logged: { bg: '#f4f0e6', fg: '#7a6234', label: 'Logged, not sent' },
};

const pill = {
  fontSize: 10.5,
  fontWeight: 800,
  padding: '3px 9px',
  borderRadius: 999,
  whiteSpace: 'nowrap',
} as const;

const notice = {
  fontSize: 12,
  lineHeight: '17px',
  borderRadius: 12,
  padding: '9px 12px',
  margin: 0,
  fontWeight: 600,
} as const;

/** The status key a move to Not a match sends. */
export const NOT_A_MATCH = 'not-a-match';

/**
 * Not a match sends nothing on its own (20 Oct 2026). The API opens a clock
 * instead, and the letter is staff's to send from this thread with the "Not
 * a match" template — where a referral's clinician, a stop request and a
 * missing address are all answered for. Every control that makes the move
 * says so in these words: before it (`after` false, in a confirmation) or
 * right after it, where the move had no confirmation.
 */
export function NoLetterNote({ after = false }: { after?: boolean }) {
  const words = (
    <>
      {after ? 'Moved to Not a match. No letter went out on its own' : 'No letter goes out on its own'}: send it
      from the conversation with the “Not a match” template.{' '}
      {after
        ? 'The clock “Send the not-a-match letter.” is now running.'
        : 'Moving them starts the clock “Send the not-a-match letter.”'}
    </>
  );
  // Before the move it sits inside a confirmation's own paragraph, so a span.
  return after ? (
    <p role="status" style={{ ...notice, background: color.tint, color: color.blueDeep }}>
      {words}
    </p>
  ) : (
    <span style={{ display: 'block' }}>{words}</span>
  );
}

export function Conversation({
  submission: r,
  fm,
  history,
  meta,
  focus,
}: {
  submission: Submission;
  fm: FormMeta;
  history: History;
  meta: MetaResponse;
  focus?: 'reply';
}) {
  const messages = useMessages(r.id);
  const send = useSendMessage();
  const setStatus = useSetStatus();
  const { can } = useAuth();

  const L = useMemo(() => makeLookup(meta), [meta]);
  const templates = templatesFor(r.form);

  const canUpdate = can('submissions:update');
  const canReply = canUpdate && !r.redacted && !!r.email;
  /*
    Null until someone picks, rather than a computed initial value: the default
    depends on whether a reply is possible at all, and a `useState` initialiser
    freezes whatever was known on the first render — a record that arrived
    before the session did would open on Internal note and stay there.
    A stop request opens on a note by design: it is answered by stopping.
  */
  const [chosen, setChosen] = useState<ComposeKind | null>(null);
  const mode: ComposeKind =
    chosen ?? (canReply && r.form !== 'stop-contacting-me' ? 'reply' : 'note');
  const setMode = setChosen;
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [filled, setFilled] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const textarea = useRef<HTMLTextAreaElement>(null);

  const provider = messages.data?.provider ?? null;
  const mailbox = messages.data?.mailbox ?? null;
  const thread = messages.data?.messages ?? [];
  const items = mergeTimeline(history, thread);

  /*
    Deferred a tick: the Modal focuses its own panel on mount, and a child
    effect runs first, so focusing here directly would be undone immediately.
  */
  useEffect(() => {
    if (focus !== 'reply' || mode !== 'reply') return;
    const t = window.setTimeout(() => {
      textarea.current?.focus();
      textarea.current?.scrollIntoView({ block: 'nearest' });
    }, 0);
    return () => window.clearTimeout(t);
  }, [focus, mode]);

  function applyTemplate(key: string) {
    const t = templates.find((x) => x.key === key);
    if (!t) return;
    const ctx = contextOf(r, L);
    setSubject(fillTemplate(t.subject, ctx));
    const next = fillTemplate(t.body, ctx);
    setBody(next);
    setFilled(next);
  }

  async function submit(advance: boolean) {
    setConfirming(false);
    setDone(null);
    try {
      const { message } = await send.mutateAsync({
        id: r.id,
        body:
          mode === 'reply'
            ? { kind: 'reply', subject: subject.trim(), body: body.trim() }
            : { kind: 'note', body: body.trim() },
      });
      setDone(
        message.delivery === null
          ? 'Note saved.'
          : message.delivery === 'sent'
            ? `Sent to ${message.to ?? r.email}.`
            : message.delivery === 'logged'
              ? 'Logged — no email provider is configured.'
              : 'The mail server refused it. It is on the thread as failed — try again, or call.',
      );
      setSubject('');
      setBody('');
      setFilled('');
      // A reply that did not go out does not move them on: the status would
      // tell the next person something nobody told the participant.
      if (advance && message.delivery !== 'failed') setStatus.mutate({ id: r.id, status: fm.primary.to });
    } catch {
      /* surfaced through send.error */
    }
  }

  const ready = body.trim().length > 0 && (mode === 'note' || subject.trim().length > 0);
  const blocked = send.isPending || !ready;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ flex: 1, minWidth: 120 }}>
          <SectionHead>Conversation</SectionHead>
        </span>
      </div>

      {messages.data?.redacted && (
        <p style={{ fontSize: 12.5, color: color.muted, margin: 0 }}>
          What was written is hidden for your role; who wrote it and when is shown.
        </p>
      )}

      {messages.isLoading ? (
        <Loading label="Loading the thread…" />
      ) : messages.error ? (
        <ErrorNote message={(messages.error as Error).message} />
      ) : (
        <>
          <Timeline items={items} meta={meta} personName={r.redacted ? null : r.name} />
          {messages.data && <AnswersLine answers={messages.data.answers} />}
        </>
      )}

      {!canUpdate ? (
        <p style={{ fontSize: 12.5, color: color.muted, margin: 0 }}>
          Your role can read the conversation but not reply.
        </p>
      ) : (
        <div
          style={{
            borderTop: `1px solid ${color.line}`,
            paddingTop: 14,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            {canReply && (
              <Chip label="Reply" active={mode === 'reply'} onClick={() => setMode('reply')} />
            )}
            <Chip
              label="Internal note"
              active={mode === 'note'}
              onClick={() => setMode('note')}
              title="Staff only. The person never sees this."
            />
          </div>

          {!canReply && (
            <p style={{ ...notice, background: color.bg, color: color.muted }}>
              {r.redacted
                ? 'Contact is hidden for your role, so a reply cannot be sent from here — notes only.'
                : 'This form did not collect an email address — notes only.'}
            </p>
          )}

          {mode === 'reply' && <ReplyCautions r={r} L={L} />}

          {mode === 'reply' && fm.phi && (
            <p style={{ ...notice, background: color.tint, color: color.blueDeep }}>
              This form carries protected health information. Keep the reply to logistics — no
              eligibility impressions, scores or internal reasons.
            </p>
          )}

          {mode === 'reply' ? (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))',
                  gap: 12,
                }}
              >
                <Field label="To">
                  <input
                    value={r.email}
                    readOnly
                    style={{ ...inputStyle, background: color.bg, color: color.muted }}
                  />
                </Field>
                {mailbox && (
                  <Field label="From">
                    <input
                      value={mailbox}
                      readOnly
                      title="The mailbox that owns this form’s stream. Their answer comes back to it."
                      style={{ ...inputStyle, background: color.bg, color: color.muted }}
                    />
                  </Field>
                )}
                {templates.length > 0 && (
                  <Field
                    label="Template"
                    hint={body && body !== filled ? '— clear the message to load one' : undefined}
                  >
                    <select
                      value=""
                      disabled={!!body && body !== filled}
                      onChange={(e) => applyTemplate(e.target.value)}
                      style={selectStyle}
                    >
                      <option value="">Start from…</option>
                      {templates.map((t) => (
                        <option key={t.key} value={t.key}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                )}
              </div>

              <Field label="Subject">
                <input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="We received your ENACT inquiry"
                  style={inputStyle}
                />
              </Field>
            </>
          ) : (
            <p style={{ ...notice, background: color.bg, color: color.muted }}>
              Internal. The person never sees this.
            </p>
          )}

          <Field label="Message">
            <textarea
              ref={textarea}
              value={body}
              rows={7}
              onChange={(e) => {
                setBody(e.target.value);
                setDone(null);
              }}
              /*
                Escape closes the dialog. With text in the box that is a lost
                draft, so the key stops here instead.
              */
              onKeyDown={(e) => {
                if (e.key === 'Escape' && body.trim()) e.stopPropagation();
              }}
              placeholder={mode === 'reply' ? 'Write the reply…' : 'What the next person needs to know…'}
              style={{ ...inputStyle, fontSize: 14, lineHeight: '22px', padding: '11px 13px', resize: 'vertical' }}
            />
          </Field>

          {mode === 'reply' && provider && !provider.configured && (
            <p style={{ ...notice, background: '#f4f0e6', color: '#7a6234' }}>
              No email provider — replies are logged, not sent.
            </p>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <Button onClick={() => void submit(false)} disabled={blocked}>
              {send.isPending ? 'Saving…' : mode === 'reply' ? 'Send reply' : 'Save note'}
            </Button>

            {mode === 'reply' && r.status !== fm.primary.to && (
              <Confirm
                open={confirming}
                title={`Send, then ${fm.primary.label.toLowerCase()}?`}
                detail={
                  meta.statuses.find((s) => s.key === fm.primary.to)?.blurb
                    ? `They will be shown: “${meta.statuses.find((s) => s.key === fm.primary.to)?.blurb}”`
                    : undefined
                }
                confirmLabel="Send"
                onConfirm={() => void submit(true)}
                onCancel={() => setConfirming(false)}
              >
                <Button
                  variant="ghost"
                  onClick={() => setConfirming(true)}
                  disabled={blocked}
                  title="You will be asked to confirm"
                  style={{ border: `1.5px solid ${color.blue}`, color: color.blue }}
                >
                  Send &amp; {fm.primary.label}
                </Button>
              </Confirm>
            )}

            {done && (
              <span style={{ fontSize: 12.5, color: color.muted, fontWeight: 600 }}>{done}</span>
            )}
          </div>

          {send.error && <ErrorNote message={(send.error as Error).message} />}
          {setStatus.error && <ErrorNote message={(setStatus.error as Error).message} />}
        </div>
      )}
    </div>
  );
}

/** A word of care where the form itself asks for one. */
function ReplyCautions({ r, L }: { r: Submission; L: ReturnType<typeof makeLookup> }) {
  if (r.form === 'stop-contacting-me') {
    return (
      <p style={{ ...notice, background: '#fdf1ee', color: color.red }}>
        They asked us to stop contacting them — reply only if the request itself needs a
        confirmation.
      </p>
    );
  }
  const fields = registryFieldsOf(r);
  const channel = fields?.['pref.channel'];
  if (r.form === 'how-to-reach-me' && typeof channel === 'string' && channel !== 'email') {
    return (
      <p style={{ ...notice, background: color.tint, color: color.blueDeep }}>
        They asked to be reached by {registryValue('pref.channel', channel, L).toLowerCase()}.
      </p>
    );
  }
  return null;
}

/** Whether what people write back is being read, and when it was last looked at. */
function AnswersLine({ answers }: { answers: NonNullable<ReturnType<typeof useMessages>['data']>['answers'] }) {
  const text = !answers.reading
    ? 'Their replies are not read into the CRM here — they stay in the mailbox.'
    : answers.error
      ? `Their replies cannot be read yet: ${answers.error}`
      : answers.checkedAt
        ? `Their replies are filed here. Mailbox last checked ${dateTime(answers.checkedAt)}.`
        : 'Their replies are filed here once the mailbox has been checked.';
  return (
    <p
      style={{
        fontSize: 12,
        color: answers.error ? color.red : color.muted,
        margin: '-4px 0 0',
        fontWeight: answers.error ? 600 : 500,
      }}
    >
      {text}
    </p>
  );
}

/** Status moves and messages on one rail, the newest dot filled. */
function Timeline({
  items,
  meta,
  personName,
}: {
  items: TimelineItem[];
  meta: MetaResponse;
  /** Who an answer is from, as the record names them; null when hidden for the role. */
  personName: string | null;
}) {
  if (!items.length) {
    return (
      <p style={{ fontSize: 13, color: color.muted, margin: 0 }}>
        Nothing yet beyond the form itself.
      </p>
    );
  }

  return (
    <div style={{ position: 'relative', paddingLeft: 18 }}>
      <span
        style={{
          position: 'absolute',
          left: 4,
          top: 6,
          bottom: 6,
          width: 1,
          background: color.line,
        }}
      />
      {items.map((item, i) => {
        const last = i === items.length - 1;
        return (
          <div key={item.kind === 'message' ? item.message.id : `h${i}`} style={{ position: 'relative', padding: '0 0 14px' }}>
            <span
              style={{
                position: 'absolute',
                left: -18,
                top: 4,
                width: 9,
                height: 9,
                borderRadius: '50%',
                background: last ? color.blue : color.white,
                border: `2px solid ${last ? color.blue : color.line}`,
                boxSizing: 'border-box',
              }}
            />
            {item.kind === 'status' ? (
              <>
                <p style={{ fontSize: 13, fontWeight: 700, color: color.ink, margin: 0 }}>
                  {item.from
                    ? `${statusLabelOf(meta, item.from)} → ${statusLabelOf(meta, item.to)}`
                    : `Received (${statusLabelOf(meta, item.to)})`}
                </p>
                <p style={{ fontSize: 11.5, color: color.muted, margin: '2px 0 0' }}>
                  {dateTime(item.at)}
                  {item.actor ? ` · ${item.actor}` : ''}
                </p>
              </>
            ) : (
              <MessageBox message={item.message} personName={personName} />
            )}
          </div>
        );
      })}
    </div>
  );
}

const kindLabel: Record<Message['kind'], string> = {
  reply: 'Reply',
  note: 'Internal note',
  receipt: 'Automatic receipt',
  inbound: 'They wrote back',
};

/** An answer from the person, set apart from what the team sent. */
const ANSWER = { bg: '#e3f1e8', fg: '#14563c', edge: '#2f8a5c' };

function MessageBox({ message: m, personName }: { message: Message; personName: string | null }) {
  if (m.kind === 'inbound') return <AnswerBox message={m} personName={personName} />;
  const tone = m.delivery ? deliveryTone[m.delivery] : undefined;
  const reply = m.kind !== 'note';
  return (
    <div
      style={{
        background: color.bg,
        border: `1px solid ${color.line}`,
        borderLeft: `3px solid ${reply ? color.blue : color.tint2}`,
        borderRadius: 12,
        padding: '10px 12px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span
          style={{
            ...pill,
            background: reply ? color.tint : color.line,
            color: reply ? color.blueDeep : color.muted,
          }}
        >
          {kindLabel[m.kind]}
        </span>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: color.ink }}>
          {m.kind === 'receipt' ? 'Sent by the form' : (m.authorName ?? 'A former staff account')}
        </span>
        <span style={{ fontSize: 11.5, color: color.muted }}>{dateTime(m.createdAt)}</span>
        <span style={{ flex: 1 }} />
        {tone && <span style={{ ...pill, background: tone.bg, color: tone.fg }}>{tone.label}</span>}
      </div>
      {m.subject && (
        <p style={{ fontSize: 13, fontWeight: 700, color: color.ink, margin: '8px 0 0' }}>
          {m.subject}
        </p>
      )}
      {(m.to || m.from) && (
        <p style={{ fontSize: 11.5, color: color.muted, margin: '2px 0 0' }}>
          {[m.to && `to ${m.to}`, m.from && `from ${m.from}`].filter(Boolean).join(' · ')}
        </p>
      )}
      {m.body !== null ? (
        <p
          style={{
            fontSize: 13,
            lineHeight: '20px',
            color: color.ink,
            margin: '6px 0 0',
            whiteSpace: 'pre-wrap',
          }}
        >
          {m.body}
        </p>
      ) : (
        <p style={{ fontSize: 12, color: color.muted, margin: '6px 0 0' }}>
          {m.kind === 'receipt'
            ? 'The form’s reviewed acknowledgment, sent when it arrived. Its words are not kept here.'
            : 'Hidden for your role.'}
        </p>
      )}
    </div>
  );
}

function AnswerBox({ message: m, personName }: { message: Message; personName: string | null }) {
  return (
    <div
      style={{
        background: color.white,
        border: `1px solid ${color.line}`,
        borderLeft: `3px solid ${ANSWER.edge}`,
        borderRadius: 12,
        padding: '10px 12px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ ...pill, background: ANSWER.bg, color: ANSWER.fg }}>{kindLabel.inbound}</span>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: color.ink }}>{personName ?? 'The person'}</span>
        <span style={{ fontSize: 11.5, color: color.muted }}>{dateTime(m.createdAt)}</span>
        <span style={{ flex: 1 }} />
        {m.isNew && <span style={{ ...pill, background: color.blue, color: color.white }}>New</span>}
      </div>
      {m.subject && (
        <p style={{ fontSize: 13, fontWeight: 700, color: color.ink, margin: '8px 0 0' }}>{m.subject}</p>
      )}
      <p style={{ fontSize: 11.5, color: color.muted, margin: '2px 0 0' }}>
        {[m.from && `from ${m.from}`, m.to && `to ${m.to}`].filter(Boolean).join(' · ')}
      </p>
      {m.body !== null ? (
        <p style={{ fontSize: 13, lineHeight: '20px', color: color.ink, margin: '6px 0 0', whiteSpace: 'pre-wrap' }}>
          {m.body || '(No text — only what was attached.)'}
        </p>
      ) : (
        <p style={{ fontSize: 12, color: color.muted, margin: '6px 0 0' }}>Hidden for your role.</p>
      )}
      {m.hasAttachments && (
        <p style={{ fontSize: 12, color: color.muted, margin: '6px 0 0', fontWeight: 600 }}>
          It came with attachments — open it in {m.to ?? 'the mailbox'} to see them.
        </p>
      )}
    </div>
  );
}
